import { Chip, useTheme } from '@mui/material'

const statusColors = {
  // Work Order statuses
  open: { bg: '#dbeafe', text: '#1e40af' },
  assigned: { bg: '#fef3c7', text: '#92400e' },
  in_progress: { bg: '#e0e7ff', text: '#3730a3' },
  on_hold: { bg: '#fecaca', text: '#991b1b' },
  completed: { bg: '#d1fae5', text: '#065f46' },
  closed: { bg: '#e5e7eb', text: '#374151' },
  cancelled: { bg: '#fecaca', text: '#991b1b' },

  // Asset statuses (A-F classification)
  a: { bg: '#d1fae5', text: '#065f46', label: 'A − Good and in use' },
  b: { bg: '#dbeafe', text: '#1e40af', label: 'B − Good but not in use' },
  c: { bg: '#fef3c7', text: '#92400e', label: 'C − In use but needs repair' },
  d: { bg: '#fef3c7', text: '#92400e', label: 'D − In use but needs maintenance' },
  e: { bg: '#fed7aa', text: '#9a3412', label: 'E − Not in use, needs repair/maintenance' },
  f: { bg: '#fecaca', text: '#991b1b', label: 'F − Not functional, needs discard' },
  // Legacy asset statuses (for backward compatibility)
  operational: { bg: '#d1fae5', text: '#065f46' },
  maintenance: { bg: '#fef3c7', text: '#92400e' },
  under_maintenance: { bg: '#fef3c7', text: '#92400e' },
  out_of_service: { bg: '#fecaca', text: '#991b1b' },
  out_for_repair: { bg: '#e0e7ff', text: '#3730a3' },
  condemned: { bg: '#374151', text: '#ffffff' },
  disposed: { bg: '#e5e7eb', text: '#374151' },
  retired: { bg: '#e5e7eb', text: '#374151' },

  // Transfer statuses
  requested: { bg: '#fef3c7', text: '#92400e' },
  in_transit: { bg: '#dbeafe', text: '#1e40af' },

  // Requisition statuses
  draft: { bg: '#e5e7eb', text: '#374151' },
  submitted: { bg: '#fef3c7', text: '#92400e' },
  under_review: { bg: '#e0e7ff', text: '#3730a3' },
  ordered: { bg: '#dbeafe', text: '#1e40af' },
  received: { bg: '#d1fae5', text: '#065f46' },

  // PM Schedule / External Repair statuses
  scheduled: { bg: '#dbeafe', text: '#1e40af' },
  overdue: { bg: '#fecaca', text: '#991b1b' },
  skipped: { bg: '#e5e7eb', text: '#374151' },
  sent_out: { bg: '#fef3c7', text: '#92400e' },
  in_repair: { bg: '#e0e7ff', text: '#3730a3' },
  ready_pickup: { bg: '#dbeafe', text: '#1e40af' },
  returned: { bg: '#d1fae5', text: '#065f46' },

  // Vehicle statuses
  available: { bg: '#d1fae5', text: '#065f46' },
  in_use: { bg: '#dbeafe', text: '#1e40af' },

  // Priority
  low: { bg: '#e5e7eb', text: '#374151' },
  medium: { bg: '#dbeafe', text: '#1e40af' },
  high: { bg: '#fef3c7', text: '#92400e' },
  critical: { bg: '#fecaca', text: '#991b1b' },
  urgent: { bg: '#fecaca', text: '#991b1b' },

  // General
  active: { bg: '#d1fae5', text: '#065f46' },
  inactive: { bg: '#e5e7eb', text: '#374151' },
  pending: { bg: '#fef3c7', text: '#92400e' },
  approved: { bg: '#d1fae5', text: '#065f46' },
  rejected: { bg: '#fecaca', text: '#991b1b' },

  // Default
  default: { bg: '#e5e7eb', text: '#374151' },
}

// Dark-mode translation keyed by the light background hex above. Each tone becomes
// a translucent tint with a bright label so the pill reads on the dark neon paper
// while keeping AA (>=4.5:1) contrast for the text.
const darkByBg = {
  '#d1fae5': { bg: 'rgba(52, 211, 153, 0.16)', text: '#6ee7b7' },
  '#dbeafe': { bg: 'rgba(96, 165, 250, 0.16)', text: '#93c5fd' },
  '#fef3c7': { bg: 'rgba(251, 191, 36, 0.16)', text: '#fcd34d' },
  '#e0e7ff': { bg: 'rgba(129, 140, 248, 0.18)', text: '#c7d2fe' },
  '#fecaca': { bg: 'rgba(248, 113, 113, 0.16)', text: '#fca5a5' },
  '#fed7aa': { bg: 'rgba(251, 146, 60, 0.16)', text: '#fdba74' },
  '#e5e7eb': { bg: 'rgba(148, 163, 184, 0.16)', text: '#cbd5e1' },
  '#374151': { bg: 'rgba(148, 163, 184, 0.24)', text: '#e5e7eb' },
}

const StatusChip = ({ status, label, size = 'small', ...props }) => {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const normalizedStatus = status?.toLowerCase().replace(/\s+/g, '_') || 'default'
  const statusConfig = statusColors[normalizedStatus] || statusColors.default
  const colors =
    isDark && darkByBg[statusConfig.bg]
      ? darkByBg[statusConfig.bg]
      : { bg: statusConfig.bg, text: statusConfig.text }
  const displayLabel = label || statusConfig.label || status?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())

  return (
    <Chip
      label={displayLabel}
      size={size}
      sx={{
        backgroundColor: colors.bg,
        color: colors.text,
        fontWeight: 500,
        textTransform: 'capitalize',
      }}
      {...props}
    />
  )
}

export default StatusChip
